import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserExternalInvestDto } from '../interfaces/user-external-invest.dto';
import { ExternalInvestResponseDto } from '../interfaces/external-invest-response.dto';

interface PaymentData {
  userId : string,
  userWallet : string,
  projectId : string,
  totalAmount : number,
  totalTokens : number
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly apiUrl = environment.api.url;

  constructor(private readonly http: HttpClient) {}

  enviarDatos(datos: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(`${this.apiUrl}/redsys/payment`, datos, httpOptions);
  }

  /* DEPRECATED */
  transferenciaBancaria(datos: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(`${this.apiUrl}/bank/create-payment`, datos, httpOptions);
  }

  investDigitalWallet(datos: PaymentData): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(`${this.apiUrl}/projects/reserve`, datos, httpOptions);
  }

  claimDividends(datos: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(`${this.apiUrl}/projects/dividends/claim`, datos, httpOptions);
  }

  getDividendRetentions(userId: string, dividendIds: string[]): Observable<{ retentions: Record<string, number> }> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<{ retentions: Record<string, number> }>(
      `${this.apiUrl}/projects/dividends/retentions`,
      { userId, dividendIds },
      httpOptions
    );
  }

  requestWithdrawal(datos: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(`${this.apiUrl}/bank/withdraw/create`, datos, httpOptions);
  }

  changeWithdrawalStatus(datos: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.patch<any>(`${this.apiUrl}/bank/withdraw/approve`, datos, httpOptions);
  }

  createWallet(id: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.patch<any>(`${this.apiUrl}/bank/create/wallet/${id}`, httpOptions);
  }

  getMovements(params: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<any>(`${this.apiUrl}/bank/movement/${params}`, httpOptions);
  }

  addBankAccount(data: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(`${this.apiUrl}/bank/account/create`, data, httpOptions);
  }

  getHistory(token: string, type: string = ' ', page: number = 1, rows: number = 10, startDate: number = 0, endDate: number = 0, amount: number | null = null) {

    const options: { headers?: HttpHeaders } = {};
    if (token) {
      options.headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    }
    return this.http.get(`${this.apiUrl}/bank/movement?type=${type}&p=${page}&n=${rows}&s=${startDate}&e=${endDate}&a=${amount}`, options);
  }

  async getUserBalance(userId: string) {
    return await firstValueFrom(this.http.get(`${this.apiUrl}/bank/user-balance/${userId}`));
  }

  async checkExternalInvestment(userEmail: string, tokenName: string, tokens: number) {
    const body = { userEmail, tokenName, tokens };
    return await firstValueFrom(this.http.post(`${this.apiUrl}/projects/check-external-investment`, body));
  }

  async reserveExternalInvestment(userId: string, projectId: string, tokens: number, isPrivateSale: boolean = false) {
    const body = {
      "userId": userId,
      "userWallet": "",
      "projectId": projectId,
      "totalAmount": 0,
      "totalTokens": tokens,
      "isExternal": true,
      "isPrivateSale": isPrivateSale
    };
    return await firstValueFrom(this.http.post(`${this.apiUrl}/projects/reserve`, body));
  }

  async checkExternalBatchInvestments(investments: UserExternalInvestDto[], tokenName: string): Promise<ExternalInvestResponseDto> {
    const body = { investments: investments, tokenName: tokenName };
    console.log('Checking external batch investments:', body);
    return await firstValueFrom(this.http.post<ExternalInvestResponseDto>(`${this.apiUrl}/projects/check-external-batch-investment`, body));
  }

  /**
   * @deprecated Use mintTokenWithoutPay cloud function instead. This method will be removed in future versions.
   * The new unified flow handles external batch investments through mintTokenWithoutPay with isExternal flag.
   */
  async reserveExternalBatchInvestment(
    investments: UserExternalInvestDto[],
    tokenName: string,
    isPrivateSale: boolean = false
  ): Promise<string[]> {
    console.warn('DEPRECATED: reserveExternalBatchInvestment is deprecated. Use mintTokenWithoutPay cloud function instead.');
    const body = { investments: investments, tokenName: tokenName, isExternal: true, isPrivateSale };
    return await firstValueFrom(this.http.post<string[]>(`${this.apiUrl}/projects/queue/batch-reserve`, body));
  }


}
